import { LineItem } from '@/hooks/useDocumentInvoiceData'
import {
  DiscrepancyCheckRequest,
  DiscrepancyCheckResponse,
  DiscrepancyType,
} from '@/types/po-discrepancies'
import { POLineItemMatch, PDPurchaseOrder } from '@/types/po-match'
import {
  FieldDiscrepancy,
  formatValue,
} from '@/components/purchase-order-view/shared'

/** Pure payload building and response mapping for the discrepancy check. No React here. */

/** The invoice-side inputs of the check payload (a subset of `DocumentInvoiceData`). */
export type DiscrepancyCheckInvoiceData = {
  id: string
  purchaseOrderNumber: string | null
  vendorId: string | null
  lineItems: LineItem[]
}

// The backend requires Decimal-parseable strings; blank/missing values mean 0
// (mirrors pd-po's own `or 0` when it builds this payload server-side).
function numericString(value: string | null | undefined): string {
  return value?.trim() ? value : '0'
}

/**
 * Builds the ad-hoc `POST /v2/pd-po/invoices/discrepancy-check` payload from
 * data the PO matching view already has, so the server skips its own
 * document and relay lookups. `include_existing_matches` stays true — that's
 * an internal DB lookup (other invoices' consumption of the same PO lines),
 * not a relay call, and keeps the semantics of the doc-id endpoint.
 */
export function buildDiscrepancyCheckPayload({
  invoice,
  purchaseOrders,
  lineItemMatches,
}: {
  invoice: DiscrepancyCheckInvoiceData
  purchaseOrders: PDPurchaseOrder[]
  lineItemMatches: POLineItemMatch[]
}): DiscrepancyCheckRequest {
  return {
    invoice: {
      id: invoice.id,
      referenced_po_number: invoice.purchaseOrderNumber || null,
      vendor_entity_id: invoice.vendorId || null,
      line_items: invoice.lineItems.map((li) => ({
        id: li.rowId,
        description: li.description ?? '',
        quantity: numericString(li.quantity),
        unit_price: numericString(li.rate),
        amount: numericString(li.amount),
      })),
    },
    purchase_orders: purchaseOrders.map((po) => ({
      entity_id: po.external_id,
      number: po.number ?? '',
      vendor_entity_id: po.vendor_id ?? '',
      line_items: po.line_items.map((li) => ({
        entity_id: li.external_id,
        description: li.description ?? '',
        unit_price: numericString(li.unit_price),
        quantity: numericString(li.quantity),
        remaining_quantity: numericString(li.remaining_quantity),
        amount: numericString(li.amount),
        remaining_amount: numericString(li.remaining_amount),
      })),
    })),
    line_matches: lineItemMatches.map((m) => ({
      invoice_line_id: m.document_row_id,
      po_entity_id: m.po_id,
      po_line_entity_id: m.po_line_item_id,
    })),
    include_existing_matches: true,
  }
}

/** Columns on a line item row (either side) that can have a discrepancy. */
export type CellColumn = 'quantity' | 'rate' | 'amount'

/**
 * Key for a cell to render with the emphasized (opened-popover) treatment, used
 * within one table.
 */
export function emphasizedCellKey(rowId: string, column: CellColumn): string {
  return `${rowId}::${column}`
}

/**
 * Every row that belongs to a discrepancy's match group, on both sides —
 * highlighted together when the cell's popover opens. For a one-to-many or
 * many-to-one match this holds every row involved, not just the two directly
 * across from each other.
 */
export type DiscrepancyGroup = {
  invoiceRowIds: string[]
  /** PO row ids — the PO line item external id. */
  poRowIds: string[]
}

/**
 * A line-level discrepancy on a single cell. Carries the structured values the
 * popover renders as a breakdown (the endpoint returns no display text — see
 * `LineDiscrepancyDetail`) plus a one-line `summary` for the hoisted header
 * FieldItem tooltip and the cell's aria-label, and the whole match `group` so
 * opening the cell can highlight every row involved.
 */
export type CellDiscrepancy = {
  type: DiscrepancyType
  column: CellColumn
  summary: string
  invoiceValue: string
  poRemaining: string
  matchedByOthers: string
  poAvailable: string
  difference: string
  group: DiscrepancyGroup
}

/** A PO number mismatch, with both numbers so the cards can show them side by side. */
export type PoNumberDiscrepancy = FieldDiscrepancy & {
  invoicePoNumber: string | null
  poNumber: string | null
}

/** Header (PO#/vendor) discrepancies, shared by the PO cards and the invoice card. */
export type HeaderDiscrepancies = {
  poNumberDiscrepancy?: PoNumberDiscrepancy
  vendorDiscrepancy?: FieldDiscrepancy
}

export type PODiscrepancies = {
  /** PO external ids with at least one header or line discrepancy. */
  poIdsWithDiscrepancies: Set<string>
  /** Header-level discrepancies, keyed by PO external id. */
  poHeaderDiscrepancies: Map<string, HeaderDiscrepancies>
  /** Discrepancies for the invoice card's PO#/vendor fields. */
  invoiceDiscrepancies: HeaderDiscrepancies
  /** Per-PO-row cell discrepancies, keyed by PO line item external id then column (severity is always 'warning' for cell-level discrepancies). */
  poRowCellDiscrepancies: Map<
    string,
    Partial<Record<CellColumn, CellDiscrepancy>>
  >
  /** Per-invoice-row cell discrepancies, keyed by document row id — the invoice-side mirror of `poRowCellDiscrepancies`. */
  invoiceRowCellDiscrepancies: Map<
    string,
    Partial<Record<CellColumn, CellDiscrepancy>>
  >
}

// The column name doubles as the human-readable label in tooltips.
const LINE_TYPE_COLUMN: Partial<Record<DiscrepancyType, CellColumn>> = {
  AMOUNT_EXCEEDS_PO: 'amount',
  RATE_EXCEEDS_PO: 'rate',
  QTY_EXCEEDS_PO: 'quantity',
  QTY_MISSING_ON_INVOICE: 'quantity',
}

/**
 * Builds the one-line summary for a line-level discrepancy (amount/rate/qty).
 * The wording is composed client-side — the endpoint only returns structured
 * types and values (see `LineDiscrepancyDetail`), never display text. Used for
 * the cell's aria-label; the popover renders the fuller breakdown from the
 * structured values (see `buildDiscrepancyBreakdown`). `poAvailable` is what's
 * actually left on the PO line (remaining minus what other invoices have
 * already consumed), hence "available".
 */
export function buildLineDiscrepancySummary(
  type: DiscrepancyType,
  invoiceValue: string,
  poAvailable: string,
): string {
  const label = LINE_TYPE_COLUMN[type]
  if (!label) {
    // Unknown/forward-compat type — fall back to a generic message rather than crash.
    return `Invoice value (${invoiceValue}) doesn't match PO value (${poAvailable})`
  }

  const isCurrency = type === 'AMOUNT_EXCEEDS_PO' || type === 'RATE_EXCEEDS_PO'
  const currency = { decimals: 2, prefix: '$' }
  const formattedInvoice = isCurrency
    ? formatValue(invoiceValue, currency)
    : formatValue(invoiceValue)
  const formattedPo = isCurrency
    ? formatValue(poAvailable, currency)
    : formatValue(poAvailable)

  if (type === 'QTY_MISSING_ON_INVOICE') {
    return `Invoice has no ${label}, but the PO's available ${label} is ${formattedPo}`
  }

  return `Invoice ${label} (${formattedInvoice}) exceeds the PO's available ${label} of ${formattedPo}`
}

/** A single label/value line in a discrepancy popover's breakdown. */
export type BreakdownRow = {
  label: string
  value: string
  /**
   * Visual weight for the two summary lines: `available` is the amount the PO
   * still allows, `over` is how far the invoice exceeds it (rendered in error
   * color). Plain input lines have no emphasis.
   */
  emphasis?: 'available' | 'over'
}

export type DiscrepancyBreakdown = {
  title: string
  rows: BreakdownRow[]
}

const DISCREPANCY_TITLE: Partial<Record<DiscrepancyType, string>> = {
  AMOUNT_EXCEEDS_PO: 'Amount exceeds PO',
  RATE_EXCEEDS_PO: 'Rate exceeds PO',
  QTY_EXCEEDS_PO: 'Quantity exceeds PO',
  QTY_MISSING_ON_INVOICE: 'Quantity missing on invoice',
}

/**
 * Turns a cell discrepancy into the popover's title and itemized breakdown:
 * invoice value, PO remaining, amount already used by other invoices (only
 * shown if it's non-zero), and what's still available. It also adds a row
 * for how far the invoice is over, unless the discrepancy is a missing
 * quantity — there the invoice billed less than the PO allows, not more.
 */
export function buildDiscrepancyBreakdown(
  detail: CellDiscrepancy,
): DiscrepancyBreakdown {
  const { type, column } = detail
  const title = DISCREPANCY_TITLE[type] ?? 'Discrepancy'

  const isCurrency = type === 'AMOUNT_EXCEEDS_PO' || type === 'RATE_EXCEEDS_PO'
  const fmt = (value: string) =>
    isCurrency
      ? formatValue(value, { decimals: 2, prefix: '$' })
      : formatValue(value)

  const rows: BreakdownRow[] = [
    { label: `Invoice ${column}`, value: fmt(detail.invoiceValue) },
    { label: 'PO remaining', value: fmt(detail.poRemaining) },
  ]
  if (parseFloat(detail.matchedByOthers) > 0) {
    rows.push({
      label: 'Used by other invoices',
      value: fmt(detail.matchedByOthers),
    })
  }
  rows.push({
    label: 'Available on PO',
    value: fmt(detail.poAvailable),
    emphasis: 'available',
  })
  // `difference` is negative for QTY_MISSING_ON_INVOICE: the invoice billed fewer units than
  // the PO has open, not more. An "Over by" row would read "Over by -2". The available row
  // above already carries the number a reviewer needs.
  if (type !== 'QTY_MISSING_ON_INVOICE') {
    rows.push({
      label: 'Over by',
      value: fmt(detail.difference),
      emphasis: 'over',
    })
  }
  return { title, rows }
}

/**
 * Maps a `DiscrepancyCheckResponse` plus the current PO list into UI-ready
 * lookups. Defensive against missing/undefined arrays, unknown discrepancy
 * types (forward-compat), and PO line ids that aren't found in any PO in the
 * current list (e.g. stale data) — all of those are skipped gracefully
 * rather than throwing.
 */
export function mapDiscrepancyResponse(
  response: DiscrepancyCheckResponse | undefined | null,
  purchaseOrders: PDPurchaseOrder[],
): PODiscrepancies {
  const discrepancies: PODiscrepancies = {
    poIdsWithDiscrepancies: new Set(),
    poHeaderDiscrepancies: new Map(),
    invoiceDiscrepancies: {},
    poRowCellDiscrepancies: new Map(),
    invoiceRowCellDiscrepancies: new Map(),
  }
  if (!response) return discrepancies

  const headerDiscrepancies = response.header_discrepancies ?? []
  const lineDiscrepancies = response.line_discrepancies ?? []

  // PO line item external id -> owning PO external id. Derived from the PO list
  // because the response's `po_entity_ids` is deduped and can't be zipped with
  // `po_line_entity_ids`.
  const poLineItemOwner = new Map<string, string>()
  purchaseOrders.forEach((po) => {
    po.line_items.forEach((li) => {
      poLineItemOwner.set(li.external_id, po.external_id)
    })
  })

  headerDiscrepancies.forEach((hd) => {
    if (hd.type === 'PO_NUMBER_MISMATCH') {
      const poNumberDiscrepancy: PoNumberDiscrepancy = {
        severity: 'warning',
        tooltip: `Invoice PO # (${
          hd.invoice_po_number || '—'
        }) doesn't match PO # (${hd.po_number || '—'})`,
        invoicePoNumber: hd.invoice_po_number || null,
        poNumber: hd.po_number || null,
      }
      if (!discrepancies.invoiceDiscrepancies.poNumberDiscrepancy) {
        discrepancies.invoiceDiscrepancies.poNumberDiscrepancy =
          poNumberDiscrepancy
      }
      if (hd.po_entity_id) {
        discrepancies.poIdsWithDiscrepancies.add(hd.po_entity_id)
        const existing =
          discrepancies.poHeaderDiscrepancies.get(hd.po_entity_id) ?? {}
        existing.poNumberDiscrepancy = poNumberDiscrepancy
        discrepancies.poHeaderDiscrepancies.set(hd.po_entity_id, existing)
      }
    } else if (hd.type === 'VENDOR_MISMATCH') {
      const tooltip = "Invoice vendor doesn't match PO vendor"
      if (!discrepancies.invoiceDiscrepancies.vendorDiscrepancy) {
        discrepancies.invoiceDiscrepancies.vendorDiscrepancy = {
          severity: 'error',
          tooltip,
        }
      }
      if (hd.po_entity_id) {
        discrepancies.poIdsWithDiscrepancies.add(hd.po_entity_id)
        const existing =
          discrepancies.poHeaderDiscrepancies.get(hd.po_entity_id) ?? {}
        existing.vendorDiscrepancy = { severity: 'error', tooltip }
        discrepancies.poHeaderDiscrepancies.set(hd.po_entity_id, existing)
      }
    }
    // Unknown/forward-compat header discrepancy type: no known UI target, skip.
  })

  lineDiscrepancies.forEach((ld) => {
    const invoiceLineIds = ld.invoice_line_ids ?? []
    const poLineEntityIds = ld.po_line_entity_ids ?? []
    const details = ld.discrepancies ?? []

    details.forEach((detail) => {
      const column = LINE_TYPE_COLUMN[detail.type]
      if (!column) return // Unknown/forward-compat type with no known cell target.

      const summary = buildLineDiscrepancySummary(
        detail.type,
        detail.invoice_value,
        detail.po_available,
      )

      // Resolve the PO-side rows of the match group, each kept with its owning
      // PO id so the PO card can be highlighted alongside the row.
      const poRows: { poId: string; rowId: string }[] = []
      poLineEntityIds.forEach((poLineId) => {
        const poId = poLineItemOwner.get(poLineId)
        if (!poId) return // PO line id not found in the current PO list — skip.
        poRows.push({ poId, rowId: poLineId })
      })

      // The whole match group, both sides — highlighted together when a cell's
      // popover opens, so one-to-many/many-to-one matches light up every row.
      const group: DiscrepancyGroup = {
        invoiceRowIds: [...invoiceLineIds],
        poRowIds: poRows.map((r) => r.rowId),
      }

      const makeCell = (): CellDiscrepancy => ({
        type: detail.type,
        column,
        summary,
        invoiceValue: detail.invoice_value,
        poRemaining: detail.po_remaining,
        matchedByOthers: detail.matched_by_others,
        poAvailable: detail.po_available,
        difference: detail.difference,
        group,
      })

      // Fan the discrepancy out to every row in the match group, on both sides.
      poRows.forEach(({ poId, rowId }) => {
        discrepancies.poIdsWithDiscrepancies.add(poId)
        const rowDiscrepancies =
          discrepancies.poRowCellDiscrepancies.get(rowId) ?? {}
        rowDiscrepancies[column] = makeCell()
        discrepancies.poRowCellDiscrepancies.set(rowId, rowDiscrepancies)
      })

      group.invoiceRowIds.forEach((invoiceLineId) => {
        const rowDiscrepancies =
          discrepancies.invoiceRowCellDiscrepancies.get(invoiceLineId) ?? {}
        rowDiscrepancies[column] = makeCell()
        discrepancies.invoiceRowCellDiscrepancies.set(
          invoiceLineId,
          rowDiscrepancies,
        )
      })
    })
  })

  return discrepancies
}
